/** Omni station the operator entered on this computer. Unset means none. */

import {
  WORKSTATION_QC,
  WORKSTATION_SHIPPING,
  writeWorkstationMode,
} from "./workstationMode";
import { isMainDashboardPath } from "./dashboardRoute";

export const STATION_CHOICE_KEY = "riverr.stationChoice";
export const STATION_CHOICE_EVENT = "riverr:station-choice";
export const STATION_NONE = "none";

export const STATION_QC = "qc";
export const STATION_NECK = "neck";
export const STATION_PACKED = "packed";
export const STATION_SHIPPING = "shipping";
export const STATION_DTG = "dtg";
export const STATION_DTF = "dtf";
export const STATION_EMBROIDERY = "embroidery";
export const STATION_DASHBOARD = "dashboard";

export const STATIONS = [
  { id: STATION_QC, label: "QC" },
  { id: STATION_NECK, label: "Neck Label" },
  { id: STATION_PACKED, label: "Packaging" },
  { id: STATION_SHIPPING, label: "Shipping" },
  { id: STATION_DTG, label: "DTG Printing" },
  { id: STATION_DTF, label: "DTF Printing" },
  { id: STATION_EMBROIDERY, label: "Embroidery" },
  { id: STATION_DASHBOARD, label: "Dashboard" },
];

const STATION_IDS = new Set(STATIONS.map((station) => station.id));

/**
 * Stations this shop can enter. Combined shops QC and ship on one
 * screen, so there is no separate Shipping station. DTG Printing
 * stays hidden until Kornit is on or a DTG printer has been added.
 */
export function stationsFor({
  splitQcShipping = false,
  dtgPrinting = false,
  packedStation = false,
  neckLabelStation = false,
} = {}) {
  return STATIONS.filter((station) => {
    if (station.id === STATION_NECK && !neckLabelStation) return false;
    if (station.id === STATION_PACKED && !packedStation) return false;
    if (station.id === STATION_SHIPPING && !splitQcShipping) return false;
    if (station.id === STATION_DTG && !dtgPrinting) return false;
    return true;
  });
}

/** Station screen the item page is embedded in, or null on the admin page. */
export function stationScreenBase(pathname) {
  const path = String(pathname || "");
  if (path === "/qc-station" || path.startsWith("/qc-station/")) {
    return "/qc-station";
  }
  if (path === "/neck-label-station" || path.startsWith("/neck-label-station/")) {
    return "/neck-label-station";
  }
  if (path === "/bins/shipping" || path.startsWith("/bins/shipping/")) {
    return "/bins/shipping";
  }
  return null;
}

/** Order details inside the station screen when there is one. */
export function stationOrderPath(pathname, orderId) {
  const id = encodeURIComponent(String(orderId || ""));
  const base = stationScreenBase(pathname);
  return base ? `${base}/orders/${id}` : `/orders/${id}`;
}

export function isStationOrderPath(pathname) {
  const base = stationScreenBase(pathname);
  return Boolean(base) && String(pathname).startsWith(`${base}/orders/`);
}

export function readStationChoice() {
  try {
    const raw = String(localStorage.getItem(STATION_CHOICE_KEY) || "").trim();
    if (STATION_IDS.has(raw)) return raw;
  } catch (_err) {
    /* private mode / SSR */
  }
  return STATION_NONE;
}

export function writeStationChoice(id) {
  const next = STATION_IDS.has(id) ? id : STATION_NONE;
  try {
    localStorage.setItem(STATION_CHOICE_KEY, next);
  } catch (_err) {
    /* ignore quota / private mode */
  }
  writeWorkstationMode(next === STATION_QC ? WORKSTATION_QC : WORKSTATION_SHIPPING);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(STATION_CHOICE_EVENT, { detail: next })
    );
  }
  return next;
}

/** Each station is its own screen. */
export function stationHref(id) {
  if (id === STATION_QC) return "/qc-station";
  if (id === STATION_NECK) return "/neck-label-station";
  if (id === STATION_PACKED) return "/packed-station";
  if (id === STATION_SHIPPING) return "/bins/shipping";
  if (id === STATION_DTG) return "/machines";
  if (id === STATION_DTF) return "/dtf-station";
  if (id === STATION_EMBROIDERY) return "/embroidery-station";
  if (id === STATION_DASHBOARD) return "/dashboard";
  return null;
}

export function isEnteredStationPath(pathname) {
  const path = String(pathname || "");
  return (
    path.startsWith("/qc-station") ||
    path.startsWith("/neck-label-station") ||
    path.startsWith("/packed-station") ||
    path === "/bins/shipping" ||
    path.startsWith("/bins/shipping/") ||
    path.startsWith("/embroidery-station") ||
    path.startsWith("/dtf-station") ||
    path === "/machines" ||
    path.startsWith("/machines/")
  );
}

export function stationFromLocation(pathname, search = "") {
  const path = String(pathname || "");
  if (path.startsWith("/qc-station")) return STATION_QC;
  if (path.startsWith("/neck-label-station")) return STATION_NECK;
  if (path.startsWith("/packed-station")) return STATION_PACKED;
  if (path === "/bins/shipping" || path.startsWith("/bins/shipping/")) {
    return STATION_SHIPPING;
  }
  if (path.startsWith("/embroidery-station")) return STATION_EMBROIDERY;
  if (path.startsWith("/dtf-station")) return STATION_DTF;
  if (path === "/machines" || path.startsWith("/machines/")) return STATION_DTG;
  if (isMainDashboardPath(path)) return STATION_DASHBOARD;
  return null;
}

export function stationLabel(id) {
  return STATIONS.find((station) => station.id === id)?.label || "";
}
